import { useState } from 'react';
import type { MatchupAnalysis } from '@/types/matchup';
import { analyzeMatchup } from '@/lib/api';
import { ApiError } from '@/lib/api';
import MatchupView from '@/components/MatchupView';

type InputMode = 'players' | 'url';

const TIME_CLASSES = [
  { value: '', label: 'Auto-detect time class' },
  { value: 'blitz', label: 'Blitz' },
  { value: 'bullet', label: 'Bullet' },
  { value: 'rapid', label: 'Rapid' },
  { value: 'daily', label: 'Daily' },
];

export default function GameAnalyzerPage() {
  const [mode, setMode] = useState<InputMode>('players');
  const [gameUrl, setGameUrl] = useState('');
  const [username1, setUsername1] = useState('');
  const [username2, setUsername2] = useState('');
  const [timeClass, setTimeClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<MatchupAnalysis | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const payload = mode === 'url'
        ? { game_url: gameUrl, time_class: timeClass || undefined }
        : { username1, username2, time_class: timeClass || undefined };
      const data = await analyzeMatchup(payload);
      setResult(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-content mu-page">
      {/* ── Input section ───────────────────────────────────── */}
      <section className="mu-input-section">
        <h1 className="mu-page-title">Matchup Analyzer</h1>
        <p className="mu-page-subtitle">
          Two players, one matchup. Paste a game URL or enter both usernames.
          You get ratings, recent form, openings, and who has the edge.
        </p>

        {/* Mode toggle */}
        <div className="mu-mode-toggle" role="group" aria-label="Input mode">
          <button
            type="button"
            className={`mu-mode-btn${mode === 'players' ? ' mu-mode-btn--active' : ''}`}
            onClick={() => setMode('players')}
          >
            Two Usernames
          </button>
          <button
            type="button"
            className={`mu-mode-btn${mode === 'url' ? ' mu-mode-btn--active' : ''}`}
            onClick={() => setMode('url')}
          >
            Game URL
          </button>
        </div>

        <form className="mu-form" onSubmit={handleSubmit} noValidate>
          {mode === 'url' ? (
            <div className="mu-field-row">
              <input
                className="chess-form-input mu-input-wide"
                type="text"
                placeholder="https://www.chess.com/game/live/..."
                value={gameUrl}
                onChange={(e) => setGameUrl(e.target.value)}
                required
                aria-label="Chess.com game URL"
              />
            </div>
          ) : (
            <div className="mu-field-row">
              <input
                className="chess-form-input"
                type="text"
                placeholder="Player 1 username"
                value={username1}
                onChange={(e) => setUsername1(e.target.value)}
                required
                aria-label="First player username"
                autoComplete="off"
                autoCapitalize="none"
              />
              <span className="mu-vs-divider" aria-hidden>vs.</span>
              <input
                className="chess-form-input"
                type="text"
                placeholder="Player 2 username"
                value={username2}
                onChange={(e) => setUsername2(e.target.value)}
                required
                aria-label="Second player username"
                autoComplete="off"
                autoCapitalize="none"
              />
            </div>
          )}

          <div className="mu-options-row">
            <select
              className="chess-form-year-select"
              value={timeClass}
              onChange={(e) => setTimeClass(e.target.value)}
              aria-label="Time class"
            >
              {TIME_CLASSES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button className="cta-button" type="submit" disabled={loading}>
              {loading ? 'Analyzing…' : 'Analyze Matchup'}
            </button>
          </div>
        </form>

        {error && (
          <p className="mu-error" role="alert">{error}</p>
        )}

        {loading && (
          <div className="mu-loading" role="status" aria-live="polite">
            <div className="mu-spinner" aria-hidden />
            <p className="mu-loading-text">
              Pulling stats from Chess.com…
            </p>
          </div>
        )}
      </section>

      {result && <MatchupView data={result} />}
    </div>
  );
}
